import {
  useListSource,
  DictionaryAutocomplete,
  useAutocompleteSource,
  useStaticListSource,
} from '@eas/common-web';
import {
  Notice,
  Ticket,
  Operator,
  Activity,
  Program,
  SpecificTarget,
} from '../../common/models';
import { ApiFilterOperation } from '@eas/common-web';
import { Permissions } from '../../common/permissions';
import { useTicketListSource } from '../../common/use-ticket-list-source';

export function useOrganisationTypes() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/organisation-types/autocomplete/full',
  });
}

export function useOrganisationTypesAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/organisation-types/autocomplete/full/all',
  });
}

export function useProjectPhases() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/project-phases/autocomplete/full',
  });
}

export function useProjectPhasesAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/project-phases/autocomplete/full/all',
  });
}

export function useRegions() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/regions/autocomplete/full',
  });
}

export function useRegionsAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/regions/autocomplete/full/all',
  });
}

export function useRegionsAllAuto() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/regions/autocomplete/all',
  });
}

export function usePrograms() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/programs/autocomplete/full',
  });
}

export function useProgramsAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/programs/autocomplete/full/all',
  });
}

export function useSpecificTargets() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/specific-targets/autocomplete/full',
  });
}

export function useFaqSpecificTargets(programs: Program[] | null = []) {
  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(
      '/api/ks/public/dictionary/specific-targets/all',
      'programIds',
      programs
    ),
    skip: (programs ?? []).length === 0,
  });
}

export function useTicketSpecificTargets(program?: Program) {
  return useTicketListSource<DictionaryAutocomplete>({
    url:
      program != null
        ? `/api/ks/public/dictionary/specific-targets/all?programIds=${encodeURIComponent(
            program.id
          )}`
        : '/api/ks/public/dictionary/specific-targets/all',
    skip: program == null,
  });
}

export function useRuleSpecificTargets(program?: Program) {
  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(
      '/api/ks/public/dictionary/specific-targets/all',
      'programIds',
      program != null ? [program] : []
    ),
  });
}

export function useSpecificTargetsAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/specific-targets/autocomplete/full/all',
  });
}

export function useSpecificTargetsAllAuto() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/specific-targets/autocomplete/all',
  });
}

export function useNotices(activity?: Activity) {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/notices/autocomplete/full',
    apiUrl: '/api/ks/notices',
    params:
      activity != undefined
        ? {
            filters: [
              {
                field: 'activities.id',
                operation: ApiFilterOperation.EQ,
                value: activity.id,
              },
            ],
          }
        : undefined,
  });
}

export function useFaqNotices(specificTargets: SpecificTarget[] | null = []) {
  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(
      '/api/ks/public/dictionary/notices/all',
      'specificTargetIds',
      specificTargets
    ),
  });
}

export function useTicketNotices(
  activity?: Activity,
  specificTargetIds: string[] = []
) {
  const specificTargetQuery = specificTargetIds
    .map((id) => `specificTargetIds=${encodeURIComponent(id)}`)
    .join('&');
  return useTicketListSource<DictionaryAutocomplete>({
    url:
      activity != null && specificTargetQuery !== ''
        ? `/api/ks/public/dictionary/notices/all/activity/${encodeURIComponent(
            activity.id
          )}?${specificTargetQuery}`
        : '/api/ks/public/dictionary/notices/all',
    apiUrl: '/api/ks/public/dictionary/notices',
    skip: activity == null || specificTargetIds.length === 0,
  });
}

export function useRuleNotices(
  specificTargets: SpecificTarget[] = [],
  skip = false
) {
  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(
      '/api/ks/public/dictionary/notices/all',
      'specificTargetIds',
      specificTargets
    ),
    skip,
  });
}

export function useNoticesAll() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/notices/autocomplete/full/all',
  });
}

export function useNoticesAllAuto() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/notices/autocomplete/all',
  });
}

export function useActivities() {
  return useListSource<DictionaryAutocomplete>({
    url: '/api/ks/activities/autocomplete/full',
  });
}

export function useFaqActivities(notices: Notice[] | null = []) {
  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(
      '/api/ks/public/dictionary/activities/all',
      'noticeIds',
      notices
    ),
  });
}

export function useTicketActivities(program?: Program) {
  return useTicketListSource<DictionaryAutocomplete>({
    url:
      program != null
        ? `/api/ks/public/dictionary/activities/all?programIds=${encodeURIComponent(
            program.id
          )}`
        : '/api/ks/public/dictionary/activities/all',
    skip: program == null,
  });
}

export function useRuleActivities(
  program?: Program,
  notices: Notice[] = [],
  skip = false
) {
  const programUrl = withParentIds(
    '/api/ks/public/dictionary/activities/all',
    'programIds',
    program != null ? [program] : []
  );

  return useTicketListSource<DictionaryAutocomplete>({
    url: withParentIds(programUrl, 'noticeIds', notices),
    skip,
  });
}

export function useActivitiesAll(parent?: Notice) {
  return useListSource<DictionaryAutocomplete>({
    url:
      parent != null
        ? `/api/ks/notices/${parent.id}/activities/autocomplete/full/all`
        : '/api/ks/activities/autocomplete/full/all',
  });
}

export function useActivitiesAllAuto(parent?: Notice) {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url:
      parent != null
        ? `/api/ks/notices/${parent.id}/activities/autocomplete/all`
        : '/api/ks/activities/autocomplete/all',
  });
}

export function useTags() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/tags/autocomplete',
  });
}

export function useTagsAll() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/tags/autocomplete/all',
  });
}

export function useActiveEditors() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/operators/autocomplete/editors',
  });
}

export function useOperatorsAll() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/operators/autocomplete/all',
  });
}

export function useRequesters() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/requesters/autocomplete',
  });
}

export function useRequestersAll() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/requesters/autocomplete/all',
  });
}

export function useTicketStates() {
  return useStaticListSource<DictionaryAutocomplete>([
    { id: 'NEW', name: 'Nový' },
    { id: 'PROCESSING', name: 'V řešení' },
    { id: 'RESOLVED', name: 'Zodpovězen' },
    { id: 'RETURNED', name: 'Vrácen k doplnění' },
  ]);
}

export function useConsultants(ticket: Ticket | null) {
  return useStaticListSource<Operator>(ticket?.consultants ?? []);
}

export function useTicketCommentInternal() {
  return useStaticListSource<DictionaryAutocomplete>([
    { id: 'internal', name: 'Interní' },
    { id: 'external', name: 'Externí' },
  ]);
}

export function usePermissions() {
  return useStaticListSource<DictionaryAutocomplete>([
    { id: Permissions.ADMINISTRATOR, name: 'Administrátor' },
    { id: Permissions.EDITOR, name: 'Editor' },
    { id: Permissions.OPERATOR, name: 'Operátor' },
    { id: Permissions.REQUESTER, name: 'Tazatel' },
  ]);
}

export function useExportTemplates() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/export/templates/autocomplete',
  });
}

export function useExportTemplatesAll() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: '/api/ks/export/templates/autocomplete/all',
  });
}

function withParentIds(
  url: string,
  parameter: string,
  parents: { id: string }[] | null | undefined
) {
  const query = (parents ?? [])
    .map(({ id }) => `${parameter}=${encodeURIComponent(id)}`)
    .join('&');

  const separator = url.includes('?') ? '&' : '?';
  return query === '' ? url : `${url}${separator}${query}`;
}
